/** @format */
"use client";
import { useState } from "react";
import { ProductInterface } from "@/types/product";
import { ProductCard } from "./product-card";
import { PromotionInterface } from "@/types/promotion";
import Link from "next/link";
import { Guitar, Grid3X3, List, Home, Music } from "lucide-react";

interface propsInterface {
  products: ProductInterface[];
  hiddenGridButton?: boolean;
}

const ProductWrapperCard = ({
  products,
  hiddenGridButton = false,
}: propsInterface) => {
  const [isGridLayout, setIsGridLayout] = useState(true);

  const toggleLayout = () => {
    setIsGridLayout(!isGridLayout);
  };

  return (
    <>
      {products && products.length > 0 ? (
        <>
          {/* Layout Toggle Button */}
          {!hiddenGridButton && (
            <div className="flex sm:hidden justify-end mb-6">
              <button
                onClick={toggleLayout}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-xl hover:bg-[var(--color-bg-hover)] hover:border-[var(--color-primary)] transition-all duration-300 shadow-[var(--shadow-default)] group"
                aria-label={
                  isGridLayout ? "Chuyển sang danh sách" : "Chuyển sang lưới"
                }>
                {isGridLayout ? (
                  <>
                    <List className="w-4 h-4 text-[var(--color-text-muted)] group-hover:text-[var(--color-primary)] transition-colors" />
                    <span className="text-sm font-medium text-[var(--color-text-primary)]">
                      Danh sách
                    </span>
                  </>
                ) : (
                  <>
                    <Grid3X3 className="w-4 h-4 text-[var(--color-text-muted)] group-hover:text-[var(--color-primary)] transition-colors" />
                    <span className="text-sm font-medium text-[var(--color-text-primary)]">
                      Lưới
                    </span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Products Container */}
          <div
            className={
              "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2"
              // isGridLayout
              //   ? "columns-2 md:columns-5 gap-2 space-y-2"
              //   : "columns-1 gap-4 space-y-4"
            }>
            {products.map((product, index) => {
              // Get the first promotion if available
              const promotion =
                product.promotionProducts &&
                product.promotionProducts.length > 0
                  ? product.promotionProducts[0].promotion
                  : undefined;

              return (
                <div
                  key={product.id || index}
                  className={isGridLayout ? "" : "w-full"}>
                  <ProductCard
                    product={product}
                    promotion={promotion}
                    isSingleColumn={!isGridLayout}
                  />
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="text-center py-20 space-y-8 max-w-md mx-auto ">
          {/* Guitar Icon with Animation */}
          <div className="relative">
            <div className="w-32 h-32 mx-auto bg-[var(--gradient-accent)] rounded-full flex items-center justify-center shadow-[var(--shadow-default)] animate-pulse">
              <Guitar className="w-16 h-16 text-[var(--color-primary)]" />
            </div>
            {/* Musical Notes Animation */}
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-[var(--color-primary)] rounded-full flex items-center justify-center animate-bounce">
              <span className="text-xs text-[var(--color-text-white)]">♪</span>
            </div>
            <div
              className="absolute -bottom-1 -left-3 w-5 h-5 bg-[var(--color-accent-green)] rounded-full flex items-center justify-center animate-bounce"
              style={{ animationDelay: "0.5s" }}>
              <span className="text-xs text-[var(--color-text-white)]">♫</span>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-[var(--color-text-primary)]">
              Chưa Có Sản Phẩm Nào Trong Danh Mục Này
            </h3>
            <p className="text-[var(--color-text-secondary)] leading-relaxed">
              Danh mục này hiện chưa có sản phẩm. Hãy khám phá các danh mục khác
              hoặc quay lại sau để tìm guitar yêu thích của bạn!
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Link
              href="/"
              className="inline-flex items-center gap-2 bg-[var(--color-bg-accent)] text-[var(--color-text-primary)] px-6 py-3 rounded-lg hover:bg-[var(--color-bg-hover)] transition-all duration-300 font-medium border border-[var(--color-border)] group">
              <Music className="w-4 h-4 group-hover:scale-110 transition-transform duration-300" />
              Về Trang Chủ
            </Link>
          </div>

          {/* Helpful Tips */}
          <div className="bg-[var(--gradient-secondary)] border border-[var(--color-border-light)] rounded-xl p-6 text-left mt-8">
            <h4 className="text-[var(--color-text-primary)] font-semibold mb-3 flex items-center gap-2">
              <Guitar className="w-4 h-4 text-[var(--color-primary)]" />
              Gợi Ý Tìm Guitar
            </h4>
            <ul className="space-y-2 text-sm text-[var(--color-text-secondary)]">
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-[var(--color-primary)] rounded-full mt-2 flex-shrink-0"></div>
                Thử tìm kiếm với từ khóa khác như "acoustic", "electric",
                "classical"
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-[var(--color-primary)] rounded-full mt-2 flex-shrink-0"></div>
                Khám phá các danh mục guitar theo thương hiệu
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-[var(--color-primary)] rounded-full mt-2 flex-shrink-0"></div>
                Liên hệ để được tư vấn guitar phù hợp
              </li>
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductWrapperCard;
